/**
 * TecorContext — single source of truth for the WebUSB label printer
 * connection so the header status pill, the workflow printer and the
 * products page all share one live state (and one remembered device).
 *
 * Also owns the Windows driver-help dialog: any print that fails to
 * claim the interface (CLAIM_FAILED) auto-opens the WinUSB/Zadig guide.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api, { withSite } from "@/lib/api";
import { logPrint } from "@/lib/printLog";
import {
  isWebUsbSupported, getPairedTecor, requestTecorDevice, printBytes, b64ToBytes, probePrinter,
} from "@/lib/webusb";
import WindowsDriverHelp from "@/components/workflow/WindowsDriverHelp";

const TecorContext = createContext(null);
const LAST_PRINTER_KEY = "haccp_last_printer";

function readLastPrinter() {
  try { return localStorage.getItem(LAST_PRINTER_KEY) || ""; } catch { return ""; }
}
function persistPrinter(name) {
  try {
    if (name) localStorage.setItem(LAST_PRINTER_KEY, name);
  } catch { /* ignore */ }
}

export function TecorProvider({ children }) {
  const supported = isWebUsbSupported();
  const [connected, setConnected] = useState(false);
  const [deviceName, setDeviceName] = useState(readLastPrinter);
  const [busy, setBusy] = useState(false);
  const [driverHelpOpen, setDriverHelpOpen] = useState(false);
  const [printerStatus, setPrinterStatus] = useState("unknown"); // unknown | ok | blocked

  // Probe the printer (open() health-check). Silently flags "blocked" and
  // pops the driver guide if Windows is holding the device.
  const selfCheck = useCallback(async ({ silent = true } = {}) => {
    try {
      await probePrinter();
      setPrinterStatus("ok");
      return true;
    } catch (e) {
      if (e && e.code === "CLAIM_FAILED") {
        setPrinterStatus("blocked");
        setDriverHelpOpen(true);
      } else {
        setPrinterStatus("unknown");
      }
      return false;
    }
  }, []);

  useEffect(() => {
    if (!supported) return undefined;
    getPairedTecor()
      .then((d) => {
        if (d) {
          setConnected(true);
          const n = d.productName || "Tecor TR-422B";
          setDeviceName(n);
          persistPrinter(n);
          selfCheck({ silent: true });
        }
      })
      .catch(() => {});
    const onDisconnect = () => setConnected(false);
    const onConnect = () =>
      getPairedTecor().then((d) => { if (d) setConnected(true); }).catch(() => {});
    // Auto-reconnect when the station computer wakes / the tab regains
    // focus: the previously-granted Tecor is re-opened without a prompt so
    // the Print Hub keeps printing after sleep.
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      getPairedTecor()
        .then((d) => {
          if (d) {
            setConnected(true);
            const n = d.productName || "Tecor TR-422B";
            setDeviceName(n);
            persistPrinter(n);
            selfCheck({ silent: true });
          } else {
            setConnected(false);
          }
        })
        .catch(() => {});
    };
    navigator.usb.addEventListener("disconnect", onDisconnect);
    navigator.usb.addEventListener("connect", onConnect);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      navigator.usb.removeEventListener("disconnect", onDisconnect);
      navigator.usb.removeEventListener("connect", onConnect);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [supported]);

  const connect = useCallback(async () => {
    const d = await requestTecorDevice();
    setConnected(true);
    const n = d.productName || "Tecor TR-422B";
    setDeviceName(n);
    persistPrinter(n);
    selfCheck({ silent: true });
    return d;
  }, [selfCheck]);

  const printB64 = useCallback(async (b64) => {
    setBusy(true);
    try {
      return await printBytes(b64ToBytes(b64));
    } catch (e) {
      if (e && e.code === "CLAIM_FAILED") setDriverHelpOpen(true);
      throw e;
    } finally {
      setBusy(false);
    }
  }, []);

  const openDriverHelp = useCallback(() => setDriverHelpOpen(true), []);

  /** Render a label server-side (render-tspl) then push it to the Tecor.
   * If no device is paired yet, prompts the picker once and retries.
   * Logs every non-test print. Callers just toast on success/failure —
   * a CLAIM_FAILED error auto-opens the Windows driver helper. */
  const printRendered = useCallback(async (siteId, payload, meta = {}) => {
    if (!isWebUsbSupported()) {
      throw new Error("WebUSB isn't supported here. Use Chrome or Edge on desktop/Android.");
    }
    const { data } = await api.post("/labels/render-tspl", payload, withSite(siteId));
    if (!data?.bytes_b64) {
      throw new Error("Couldn't render label \u2014 turn off Training Mode to print.");
    }
    const b64 = data.bytes_b64;
    setBusy(true);
    try {
      try {
        await printBytes(b64ToBytes(b64));
      } catch (e) {
        // Robust: match a stable error code (not a localised message) so
        // an unpaired printer triggers the one-time picker + retry.
        if (e && e.code === "NO_DEVICE") {
          await connect();
          await printBytes(b64ToBytes(b64));
        } else {
          throw e;
        }
      }
    } catch (e) {
      if (e && e.code === "CLAIM_FAILED") setDriverHelpOpen(true);
      throw e;
    } finally {
      setBusy(false);
    }
    if (meta.labelType && meta.labelType !== "test") {
      logPrint(siteId, {
        product_name: meta.productName,
        label_type: meta.labelType,
        method: "tecor",
        copies: meta.copies || payload.copies || 1,
        payload,
      });
    }
    return { ok: true };
  }, [connect]);

  return (
    <TecorContext.Provider value={useMemo(() => ({
      supported, connected, deviceName, busy, printerStatus,
      connect, printB64, printRendered, openDriverHelp, selfCheck,
    }), [supported, connected, deviceName, busy, printerStatus, connect, printB64, printRendered, openDriverHelp, selfCheck])}>
      {children}
      <WindowsDriverHelp open={driverHelpOpen} onOpenChange={setDriverHelpOpen} />
    </TecorContext.Provider>
  );
}

export function useTecor() {
  const ctx = useContext(TecorContext);
  if (!ctx) {
    return {
      supported: false, connected: false, deviceName: "", busy: false,
      printerStatus: "unknown",
      connect: async () => {}, printB64: async () => {},
      printRendered: async () => {}, openDriverHelp: () => {},
      selfCheck: async () => false,
    };
  }
  return ctx;
}
